/* Copyright (c) 2026. Amazon.com, Inc. or its affiliates. All rights reserved. */

// Import the class from the vendor package (not the heavy `@/service/gql`
// adapter) so this detector stays a dependency-light leaf; `@/service/gql`
// re-exports the same class, so `instanceof` matches app-thrown errors either way.
import { GqlError } from '@amzn/amp-gql'

/**
 * Auth/entitlement denial detection: the Firefly transport surfaces an
 * unauthorized (anonymous or tier-denied) content request as a `GqlError`
 * whose printed message carries the upstream text — `Unauthorized` or a
 * `401` code. This is the WIRE auth signal, not network-vs-server error
 * classification.
 */
export function isUnauthorizedGqlError(err: unknown): err is GqlError {
    if (!(err instanceof GqlError)) return false
    const msg = err.message
    return msg.includes('Unauthorized') || msg.includes('401')
}

/**
 * Session-expiry detection: a genuine transport-level 401 (the whole HTTP
 * response was rejected as unauthenticated, not a resolver returning content
 * denied to this customer). `runGqlFetch` only sets `httpStatus` when
 * converting a non-2xx `RequestError` response — a resolver-level denial
 * (anonymous access, tier gating) resolves 200 OK with a `result.errors` body
 * instead and never carries it. That split is what lets a mid-session token
 * expiry (real 401) trigger sign-out while an anonymous or tier-denied
 * content request — which also satisfies `isUnauthorizedGqlError` — does not.
 */
export function isSessionExpiredGqlError(err: unknown): err is GqlError {
    return err instanceof GqlError && err.httpStatus === 401
}

// Firefly server-fault codes. A 5xx loses its wire status in the
// RequestError → GqlError conversion (see runGqlFetch), so the Firefly
// error code is the only server-fault signal left on the surfaced GqlError.
const SERVER_FAULT_CODES: ReadonlySet<string> = new Set(['INTERNAL', 'SERVICE_ERROR'])

/**
 * Server-fault detection: a Firefly 5xx surfaces as a `GqlError` whose first
 * error carries an `INTERNAL`/`SERVICE_ERROR` extensions code.
 */
export function isServerFaultGqlError(err: unknown): err is GqlError {
    if (!(err instanceof GqlError)) return false
    return err.errors.some((e) => {
        const code = (e.extensions as { code?: unknown } | undefined)?.code
        return typeof code === 'string' && SERVER_FAULT_CODES.has(code)
    })
}

/**
 * CloudQueue rate limiting: the gateway answers 200 OK with an error whose
 * `extensions.payload.__type` names `RateLimitExceededException`, and nulls the
 * connection it could not resolve.
 */
export function isRateLimitedGqlError(err: unknown): err is GqlError {
    if (!(err instanceof GqlError)) return false
    return err.errors.some((e) => {
        const type = (e.extensions?.payload as { __type?: unknown } | undefined)?.__type
        return typeof type === 'string' && type.includes('RateLimitExceededException')
    })
}
